/**
 * Renewals & Drift admin tab — surfaces two things that previously
 * required SQL to inspect:
 *   1. Upcoming 90/60/30-day renewal reminders (when they'll fire,
 *      who they go to, whether they've already fired).
 *   2. Proposals whose recipient email has drifted from the client's
 *      current PM/billing/primary contacts.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar, Warning, ArrowsClockwise, Eye, FileText } from "@phosphor-icons/react";
import { useAuth } from "@/lib/AuthContext";
import { formatLocal } from "@/lib/timezone";
import { safeSrcDoc } from "@/lib/safeHtml";

function MilestoneChip({ milestone, fireState }) {
  const colors = {
    live:   { bg: "#FEE2E2", text: "#991B1B", label: "Will fire next" },
    future: { bg: "#F0F7FF", text: "#1E40AF", label: "Not yet" },
    passed: { bg: "#F5F5F5", text: "#737373", label: "Already passed" },
  }[fireState] || {};
  return (
    <span
      title={colors.label}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ background: colors.bg, color: colors.text }}
      data-testid={`milestone-${milestone}-${fireState}`}
    >
      {milestone}d {fireState === "live" ? "▶" : ""}
    </span>
  );
}

function ApprovalChip({ status }) {
  const map = {
    held_for_approval: { bg: "#FEF3C7", text: "#92400E", label: "Awaiting approval" },
    sent: { bg: "#F0FDF4", text: "#166534", label: "Sent" },
    failed: { bg: "#FEE2E2", text: "#991B1B", label: "Send failed" },
    skipped_no_email: { bg: "#F5F5F5", text: "#737373", label: "No email on file" },
  };
  const c = map[status];
  if (!c) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
      style={{ background: c.bg, color: c.text }}
      data-testid={`approval-status-${status}`}
    >
      {c.label}
    </span>
  );
}

function RenewalsPanel() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], horizon_days: 100 });
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [approving, setApproving] = useState(null); // `${client_id}:${milestone}` while in flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/renewal-reminders/upcoming", { params: { days: 120 } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approveSend = async (client_id, milestone, clientName) => {
    if (!window.confirm(
      `Send the ${milestone}-day renewal email to ${clientName}?\n\n` +
      "This transmits an external email to the client's contact on file. " +
      "This action cannot be undone."
    )) return;
    const key = `${client_id}:${milestone}`;
    setApproving(key);
    try {
      const r = await api.post("/renewal-reminders/approve-send", null,
        { params: { client_id, milestone } });
      toast.success(`Renewal email sent to ${r.data.recipient_email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setApproving(null);
  };

  // Group by client for the table render.
  const byClient = {};
  for (const i of data.items) {
    if (!byClient[i.client_id]) {
      byClient[i.client_id] = {
        client_id: i.client_id,
        client_name: i.client_name,
        expiry_date: i.expiry_date,
        days_left: i.days_left,
        recipient_email: i.recipient_email,
        milestones: {},
      };
    }
    byClient[i.client_id].milestones[i.milestone_days] = i;
  }
  const rows = Object.values(byClient).sort((a, b) => a.days_left - b.days_left);

  const openPreview = async (client_id, milestone) => {
    try {
      const r = await api.get("/renewal-reminders/preview-html", {
        params: { client_id, milestone },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  return (
    <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="renewals-panel">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] flex items-center gap-1.5">
            <Calendar size={14} />Upcoming renewal reminders
          </h2>
          <p className="text-xs text-[#525252] mt-0.5 leading-snug max-w-2xl">
            90/60/30-day heads-up emails to the PM/billing contact. The
            scheduler flags a reached milestone once per business day at
            09:00 PT, but never sends automatically — an admin must
            review and click <strong>Approve &amp; Send</strong> for the
            client to actually receive it.
          </p>
        </div>
        <Button onClick={load} disabled={loading} size="sm"
                className="bg-white border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] rounded-none h-8 px-3 text-[11px] uppercase tracking-wider"
                data-testid="renewals-refresh">
          <ArrowsClockwise size={12} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {rows.length === 0 && !loading && (
        <p className="text-xs text-[#A3A3A3]">No upcoming renewals in the next 120 days.</p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto border border-[#F5F5F5]">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA]">
              <tr className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#A3A3A3]">
                <th className="text-left px-3 py-2.5">Client</th>
                <th className="text-left px-3 py-2.5">Expires</th>
                <th className="text-left px-3 py-2.5">Days left</th>
                <th className="text-left px-3 py-2.5">Recipient</th>
                <th className="text-left px-3 py-2.5">Milestones</th>
                <th className="text-left px-3 py-2.5">Approval</th>
                <th className="text-right px-3 py-2.5">Preview</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => {
                const next = [30, 60, 90].find(m => c.milestones[m]?.fire_state === "live");
                const nextStatus = next && c.milestones[next]?.last_status;
                const approveKey = next ? `${c.client_id}:${next}` : null;
                return (
                  <tr key={c.client_id} className="border-t border-[#F5F5F5]"
                      data-testid={`renewal-row-${c.client_id}`}>
                    <td className="px-3 py-2.5 align-top">
                      <a href={`/clients/${c.client_id}`} className="text-[#0055FF] font-bold">
                        {c.client_name}
                      </a>
                    </td>
                    <td className="px-3 py-2.5 align-top whitespace-nowrap text-[#525252]">
                      {c.expiry_date}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <span className={`font-bold ${c.days_left <= 30 ? "text-[#DC2626]" : c.days_left <= 60 ? "text-[#D97706]" : "text-[#525252]"}`}>
                        {c.days_left}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 align-top text-[11px] break-all">
                      {c.recipient_email || <span className="italic text-[#DC2626]">no email on file</span>}
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <div className="flex flex-wrap gap-1">
                        {[90, 60, 30].map(m => c.milestones[m] && (
                          <MilestoneChip key={m} milestone={m} fireState={c.milestones[m].fire_state} />
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-top">
                      <div className="flex flex-col items-start gap-1.5">
                        {nextStatus && <ApprovalChip status={nextStatus} />}
                        {nextStatus === "held_for_approval" && (
                          <Button
                            onClick={() => approveSend(c.client_id, next, c.client_name)}
                            disabled={approving === approveKey}
                            size="sm"
                            className="bg-[#0055FF] text-white hover:bg-[#0040C0] rounded-none h-7 px-2 text-[10px] uppercase tracking-wider"
                            data-testid={`renewal-approve-${c.client_id}`}>
                            {approving === approveKey ? "Sending…" : "Approve & Send"}
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 align-top text-right whitespace-nowrap">
                      {next && (
                        <Button onClick={() => openPreview(c.client_id, next)} size="sm"
                                className="bg-white border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] rounded-none h-7 px-2 text-[10px] uppercase tracking-wider"
                                data-testid={`renewal-preview-${c.client_id}`}>
                          <Eye size={10} className="mr-1" />Preview {next}d
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-3xl w-[92vw] p-0 gap-0 max-h-[90vh] flex flex-col">
          <DialogHeader className="px-5 py-3.5 border-b border-[#E5E5E5] shrink-0">
            <DialogTitle className="font-bold tracking-tight text-base">
              Renewal reminder preview · {preview?.client_name}
            </DialogTitle>
            <p className="text-[11px] text-[#525252] mt-1">
              To: <strong>{preview?.recipient_email}</strong> · Subject: <strong>{preview?.subject}</strong>
            </p>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-auto bg-[#F5F5F5]">
            {preview && (
              <iframe title="renewal-preview" srcDoc={safeSrcDoc(preview.html)}
                      sandbox=""
                      className="w-full h-full min-h-[60vh] border-0"
                      data-testid="renewal-preview-iframe" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DriftPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pausing, setPausing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/proposals/drift-audit");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Drift audit failed");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const pauseDrifted = async () => {
    if (!data?.items?.length) return;
    const targets = data.items.filter(i => i.next_reminder_at);
    if (targets.length === 0) {
      toast.info("No drifted proposals have a scheduled next reminder.");
      return;
    }
    if (!window.confirm(
      `Pause reminders for ${targets.length} drifted proposals?\n\n` +
      "This sets next_reminder_at = NULL so the scheduler stops nagging " +
      "the stale recipient. You can still resume any reminder individually " +
      "from the proposal detail page."
    )) return;
    setPausing(true);
    try {
      const r = await api.post("/proposals/drift-audit/pause-reminders", null, {
        params: { dry_run: false },
      });
      toast.success(`Paused reminders on ${r.data.paused} proposal(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pause failed");
    }
    setPausing(false);
  };

  return (
    <Card className="border border-[#E5E5E5] rounded-none p-5" data-testid="drift-panel">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] flex items-center gap-1.5">
            <Warning size={14} />Proposal recipient drift
          </h2>
          <p className="text-xs text-[#525252] mt-0.5 leading-snug max-w-2xl">
            Sent proposals whose recipient email no longer matches the
            client's current PM, billing, or primary contact. Inline drift
            warnings already exist on the proposal detail page — this view
            lists all of them across the system so you can fix a stale
            batch in one pass.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.items?.length > 0 && (
            <Button onClick={pauseDrifted} disabled={pausing} size="sm"
                    className="bg-white border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white rounded-none h-8 px-3 text-[11px] uppercase tracking-wider"
                    data-testid="drift-pause-all">
              Pause reminders on drifted
            </Button>
          )}
          <Button onClick={load} disabled={loading} size="sm"
                  className="bg-white border border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] rounded-none h-8 px-3 text-[11px] uppercase tracking-wider"
                  data-testid="drift-refresh">
            <ArrowsClockwise size={12} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap gap-4 mb-4 text-xs">
          <span className="text-[#737373]">
            <strong className="text-[#0A0A0A]">{data.summary.checked}</strong> sent proposals scanned
          </span>
          <span className="text-[#737373]">
            <strong className={data.summary.drifted > 0 ? "text-[#DC2626]" : "text-[#0A0A0A]"}>
              {data.summary.drifted}
            </strong> drifted
          </span>
          <span className="text-[#737373]">
            <strong className="text-[#0A0A0A]">{data.summary.in_sync}</strong> in sync
          </span>
        </div>
      )}

      {data?.items?.length === 0 && !loading && (
        <div className="text-[11px] text-[#166534] bg-[#F0FDF4] border border-[#BBF7D0] px-3 py-2 inline-flex items-center gap-2">
          <FileText size={12} weight="fill" />
          No drift detected — every sent proposal currently aligns with its client's contacts.
        </div>
      )}

      {data?.items?.length > 0 && (
        <div className="overflow-x-auto border border-[#F5F5F5]">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA]">
              <tr className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#A3A3A3]">
                <th className="text-left px-3 py-2.5">Proposal</th>
                <th className="text-left px-3 py-2.5">Client</th>
                <th className="text-left px-3 py-2.5">Current recipient</th>
                <th className="text-left px-3 py-2.5">Suggested ({"per current client record"})</th>
                <th className="text-left px-3 py-2.5">Source</th>
                <th className="text-left px-3 py-2.5">Next reminder</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(i => (
                <tr key={i.quote_id} className="border-t border-[#F5F5F5]"
                    data-testid={`drift-row-${i.quote_id}`}>
                  <td className="px-3 py-2.5 align-top whitespace-nowrap">
                    <a href={`/proposals/${i.quote_id}`} className="text-[#0055FF] font-bold">
                      {i.quote_number}
                    </a>
                  </td>
                  <td className="px-3 py-2.5 align-top">{i.client_name}</td>
                  <td className="px-3 py-2.5 align-top break-all text-[#525252]">
                    {i.current_recipient_email}
                  </td>
                  <td className="px-3 py-2.5 align-top break-all">
                    <strong className="text-[#0A0A0A]">{i.suggested_email}</strong>
                    {i.suggested_name && <span className="text-[#737373] block text-[11px]">{i.suggested_name}</span>}
                  </td>
                  <td className="px-3 py-2.5 align-top">
                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#F0F7FF] text-[#1E40AF]">
                      {i.drift_source.replace("_email", "")}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 align-top whitespace-nowrap text-[11px] text-[#525252]">
                    {i.next_reminder_at ? String(i.next_reminder_at).slice(0, 10) : <span className="italic text-[#A3A3A3]">paused</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function RenewalsAndDriftTab() {
  const [sub, setSub] = useState("renewals");
  return (
    <div className="space-y-4" data-testid="renewals-drift-tab">
      <div className="flex gap-0 border-b border-[#E5E5E5]">
        {[
          { id: "renewals", label: "Renewal reminders" },
          { id: "drift", label: "Proposal drift" },
        ].map(t => (
          <button key={t.id} onClick={() => setSub(t.id)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${sub === t.id ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#525252] hover:text-[#0A0A0A]"}`}
                  data-testid={`renewals-drift-sub-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === "renewals" && <RenewalsPanel />}
      {sub === "drift" && <DriftPanel />}
    </div>
  );
}
